import type { ClientSubscriptionView } from '@app/components/RecordModal/components/RecordDetails/components/ClientSubscriptions/utils';
import type {
  Service,
  ServiceGroup,
} from '@app/components/RecordModal/components/RecordDetails/types';
import {
  createEmptyService,
  normalizeClientSubscriptionId,
} from '@app/components/RecordModal/components/RecordDetails/utils/recordServices';

interface ApplyClientSubscriptionProps {
  /** Строки «Услуг» записи сейчас */
  services: Service[];
  subscription: ClientSubscriptionView;
  /** Услуги выбранного исполнителя */
  serviceOptions: ServiceGroup[];
}

interface ApplyClientSubscriptionResult {
  /** Новые строки «Услуг», null — форма не меняется */
  services: Service[] | null;
  /** Услуги абонемента, которых исполнитель не оказывает */
  notProvidedNames: string[];
  /** Услуги абонемента, которые уже есть в записи */
  alreadyAddedNames: string[];
}

/**
 * Клик по абонементу клиента в окне записи (ТЗ us/11427). Первый клик
 * добавляет по одной единице каждой услуги абонемента, которую оказывает
 * исполнитель: цена 0, исключена из скидки лояльности. Предел количества
 * считает calculateSubscriptionQuantityLimit по свежему остатку абонемента.
 * Повторный клик убирает услуги этого абонемента.
 * Одна услуга — одна строка записи (beauty-crm LogicRecords.cs:874-877),
 * поэтому услугу, которая уже есть в записи, с абонемента не добавляем.
 */
export const applyClientSubscription = ({
  services,
  subscription: { clientSubscriptionId, services: subscriptionServices },
  serviceOptions,
}: ApplyClientSubscriptionProps): ApplyClientSubscriptionResult => {
  const isApplied = services.some(
    (service) =>
      normalizeClientSubscriptionId(service?.clientSubscriptionId) ===
      clientSubscriptionId
  );

  if (isApplied) {
    const restServices = services.filter(
      (service) =>
        normalizeClientSubscriptionId(service?.clientSubscriptionId) !==
        clientSubscriptionId
    );

    return {
      services: restServices.length ? restServices : [createEmptyService()],
      notProvidedNames: [],
      alreadyAddedNames: [],
    };
  }

  const serviceOptionsById = new Map(
    serviceOptions
      .flatMap(({ options }) => options)
      .map((option) => [String(option.value), option])
  );
  const selectedServiceIds = new Set(
    services.map((service) => service?.service).filter(Boolean)
  );
  const addedServices: Service[] = [];
  const notProvidedNames: string[] = [];
  const alreadyAddedNames: string[] = [];

  subscriptionServices.forEach(({ clientServiceId, name }) => {
    const serviceId = String(clientServiceId);
    const option = serviceOptionsById.get(serviceId);

    if (!option) {
      notProvidedNames.push(name);
      return;
    }

    if (selectedServiceIds.has(serviceId)) {
      alreadyAddedNames.push(name);
      return;
    }

    addedServices.push({
      ...createEmptyService(),
      service: serviceId,
      name,
      duration: option.duration,
      quantity: 1,
      isExcludedFromLoyaltyDiscount: true,
      clientSubscriptionId,
    });
  });

  return {
    services: addedServices.length
      ? [...services.filter((service) => service?.service), ...addedServices]
      : null,
    notProvidedNames,
    alreadyAddedNames,
  };
};

/** Уведомление об услугах абонемента, которые уже есть в записи */
export const getAlreadyAddedNotification = (names: string[]): string =>
  names.length === 1
    ? `Услуга ${names[0]} уже есть в записи. С абонемента её не добавили`
    : `Услуги ${names.join(
        ', '
      )} уже есть в записи. С абонемента их не добавили`;
